import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ColDef } from 'ag-grid-community';
import Checkbox from './Checkbox';
import Chips from './Chips';
import ClientSideAgGrid from './customAgGrid/ClientSideAgGrid';
import CustomAgMultiSelectFilter from './customAgGrid/CustomAgMultiSelectFilter';
import CustomAgNumberFilter from './customAgGrid/CustomAgNumberFilter';

// Custom cell renderer for single selection checkbox
const SingleSelectCheckboxRenderer = ({ data, selectedProduct, onProductSelect }: any) => {
  return (
    <Checkbox checked={selectedProduct === data.id} onChange={() => onProductSelect(data.id)} />
  );
};

interface MasterCatalogueTableProps {
  data: any[];
  loading: boolean;
  selectedProduct: number | null;
  onProductSelect: (_productId: number) => void;
  className?: string;
}

const MasterCatalogueTable: React.FC<MasterCatalogueTableProps> = ({
  data,
  loading,
  selectedProduct,
  onProductSelect,
  className = 'master-catalogue-grid',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);

  const columns: ColDef[] = [
    {
      field: 'select',
      headerName: t('table.columns.select'),
      width: 90,
      maxWidth: 90,
      minWidth: 90,
      flex: 0,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => (
        <SingleSelectCheckboxRenderer
          data={params.data}
          selectedProduct={selectedProduct}
          onProductSelect={onProductSelect}
        />
      ),
    },
    {
      field: 'id',
      headerName: t('table.columns.id'),
      width: 100,
      maxWidth: 100,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgNumberFilter,
      sortable: true,
    },
    {
      field: 'productName',
      headerName: t('table.columns.productName'),
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
      sortable: true,
    },
    {
      field: 'maker',
      headerName: t('table.columns.maker'),
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
      sortable: true,
    },
    {
      field: 'category',
      headerName: t('table.columns.category'),
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
      sortable: true,
    },
    {
      field: 'type',
      headerName: t('table.columns.type'),
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
      sortable: true,
    },
    {
      field: 'unit',
      headerName: t('table.columns.unit'),
      width: 150,
      maxWidth: 120,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
      sortable: true,
    },
    {
      field: 'status',
      headerName: t('table.columns.status'),
      width: 150,
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
      sortable: true,
      cellRenderer: (params: any) => <Chips width="100px" label={params.data.status} />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {/* <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
                    Master Catalogue Products
                </Typography> */}
        <Box></Box>
        {isFilterActive && (
          <Typography
            onClick={() => setResetFiltersSignal(true)}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: theme.palette.primary.main,
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {t('filters.clearAll')}
          </Typography>
        )}
      </Box>
      <ClientSideAgGrid
        isInModal={true}
        rowData={data}
        columnDefs={columns}
        loading={loading}
        className={className}
        onFiltersActiveChange={(active: boolean) => {
          setIsFilterActive(active);
          setResetFiltersSignal(false);
        }}
        resetFiltersSignal={resetFiltersSignal}
        onCellClicked={(params: any) => {
          // Only toggle selection when clicking on non-checkbox cells
          // to avoid double-toggle (checkbox onChange + row click)
          if (params.data && params.data.id && params.column.colId !== 'select') {
            onProductSelect(params.data.id);
          }
        }}
      />
    </Box>
  );
};

export default MasterCatalogueTable;
